import { useState } from "react";
import { adminLogin, fetchLeads, updateLeadStatus } from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_LABEL = {
  tax: "Tax Planning",
  income: "Future Income & Retirement Planning",
  asset: "Asset Management",
  insurance: "Insurance & Protection",
  legacy: "Legacy & Estate Planning",
};
const CATEGORY_MAX = { tax: 20, income: 25, asset: 25, insurance: 15, legacy: 15 };
const CATEGORY_ORDER = ["tax", "income", "asset", "insurance", "legacy"];

const ALLOWED_STATUSES = ["New", "Contacted", "Meeting Scheduled", "Closed / Completed"];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    // Always render Eastern Time (America/New_York) so DST is handled automatically.
    const date = d.toLocaleDateString("en-US", {
      timeZone: "America/New_York",
      month: "short", day: "numeric", year: "numeric",
    });
    const time = d.toLocaleTimeString("en-US", {
      timeZone: "America/New_York",
      hour: "numeric", minute: "2-digit",
    });
    // Compute the correct EST/EDT abbreviation for this instant.
    const tz = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      timeZoneName: "short",
    })
      .formatToParts(d)
      .find((p) => p.type === "timeZoneName")?.value || "ET";
    return `${date} · ${time} ${tz}`;
  } catch {
    return "—";
  }
};

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadState, setLoadState] = useState("idle"); // idle | loading | ok | error
  const [selected, setSelected] = useState(null);

  const loadLeads = async (pwd) => {
    setLoadState("loading");
    try {
      const data = await fetchLeads(pwd);
      setLeads(data);
      setLoadState("ok");
    } catch {
      setLoadState("error");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminLogin(password);
      setAuthed(true);
      await loadLeads(password);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) toast.error(detail || "Too many login attempts. Please try again later.");
      else toast.error("Invalid password.");
    } finally {
      setBusy(false);
    }
  };

  const signOut = () => {
    setAuthed(false);
    setPassword("");
    setLeads([]);
    setSelected(null);
    setLoadState("idle");
  };

  const onStatusChange = async (leadId, newStatus) => {
    try {
      await updateLeadStatus(password, leadId, newStatus);
      setLeads((prev) => prev.map((l) => (l.lead_id === leadId ? { ...l, status: newStatus } : l)));
      if (selected?.lead_id === leadId) setSelected({ ...selected, status: newStatus });
      toast.success("Status updated.");
    } catch {
      toast.error("Could not update status.");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#F5F8FC" }}>
      <PrivateAdminHeader authed={authed} onSignOut={signOut} />
      <main className="summit-container py-10">
        {!authed ? (
          <LoginPanel password={password} setPassword={setPassword} busy={busy} onSubmit={login} />
        ) : (
          <LeadsPanel
            leads={leads}
            loadState={loadState}
            onRetry={() => loadLeads(password)}
            onView={setSelected}
            onStatusChange={onStatusChange}
          />
        )}
        {selected && (
          <LeadModal
            lead={selected}
            onClose={() => setSelected(null)}
            onStatusChange={(s) => onStatusChange(selected.lead_id, s)}
          />
        )}
      </main>
    </div>
  );
}

/* ---------- Private admin header (no public nav) ---------- */
function PrivateAdminHeader({ authed, onSignOut }) {
  return (
    <header style={{ background: "#1E1968", borderBottom: "1px solid #17114f" }} data-testid="admin-private-header">
      <div className="summit-container flex items-center justify-between" style={{ minHeight: 60 }}>
        <div style={{ color: "#fff", fontSize: "0.98rem", fontWeight: 500, letterSpacing: "0.02em" }}>
          Financial Future Readiness — Admin
        </div>
        {authed && (
          <button
            onClick={onSignOut}
            data-testid="admin-signout-btn"
            style={{
              background: "transparent", color: "#fff",
              border: "1px solid rgba(255,255,255,0.35)",
              padding: "0.4rem 0.9rem", fontSize: "0.85rem", letterSpacing: "0.02em",
              cursor: "pointer",
            }}
          >
            Sign Out
          </button>
        )}
      </div>
    </header>
  );
}

/* ---------- Login panel ---------- */
function LoginPanel({ password, setPassword, busy, onSubmit }) {
  return (
    <div className="max-w-md">
      <h1 className="font-serif" style={{ color: "#1E1968", fontSize: "2rem", fontWeight: 600 }}>Admin Access</h1>
      <p className="mt-2 text-sm" style={{ color: "#3a4d70" }}>Enter the admin password to view submitted leads.</p>
      <form className="mt-6 space-y-4" onSubmit={onSubmit}>
        <input
          type="password" placeholder="Password" className="summit-input" value={password}
          onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input"
        />
        <button className="btn-primary" disabled={busy} data-testid="admin-login-btn">
          {busy ? "Verifying…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}

/* ---------- Leads list ---------- */
function LeadsPanel({ leads, loadState, onRetry, onView, onStatusChange }) {
  if (loadState === "loading") {
    return (
      <div className="py-16 text-center" data-testid="leads-loading">
        <p className="text-base" style={{ color: "#3a4d70" }}>Loading leads…</p>
      </div>
    );
  }
  if (loadState === "error") {
    return (
      <div className="py-16 text-center" data-testid="leads-error">
        <p className="text-base" style={{ color: "#7a2b2b" }}>We couldn't load the lead list. Please try again.</p>
        <button className="btn-primary mt-4" onClick={onRetry} data-testid="leads-retry-btn">Retry</button>
      </div>
    );
  }
  if (leads.length === 0) {
    return (
      <div className="py-16 text-center" data-testid="leads-empty">
        <p className="text-base" style={{ color: "#3a4d70" }}>No leads have been submitted yet.</p>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <h1 className="font-serif" style={{ color: "#1E1968", fontSize: "1.75rem", fontWeight: 600 }}>Submitted Leads</h1>
        <div className="text-sm" style={{ color: "#173A6A" }}>{leads.length} total</div>
      </div>

      <div className="mt-6 overflow-x-auto bg-white" style={{ border: "1px solid #e6edf5" }}>
        <table className="w-full text-sm" data-testid="leads-table">
          <thead>
            <tr style={{ background: "#eaf2fa", color: "#173A6A" }}>
              <Th>Lead ID</Th>
              <Th>Name</Th>
              <Th>Contact</Th>
              <Th>Readiness Score</Th>
              <Th>Intent</Th>
              <Th>Priority</Th>
              <Th>Status</Th>
              <Th>Submitted</Th>
              <Th>Details</Th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-b" style={{ borderColor: "#eaf2fa" }} data-testid={`lead-row-${l.lead_id}`}>
                <Td>
                  <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.8rem", color: "#1E1968" }}>
                    {l.lead_id || "—"}
                  </span>
                </Td>
                <Td>{l.contact.first_name} {l.contact.last_name}</Td>
                <Td>
                  <div style={{ lineHeight: 1.35 }}>
                    <div>{l.contact.email}</div>
                    {l.contact.phone && <div style={{ color: "#3a4d70", fontSize: "0.75rem" }}>{l.contact.phone}</div>}
                  </div>
                </Td>
                <Td>
                  <span data-testid={`lead-readiness-${l.lead_id}`}>
                    <strong>{l.trail_score}</strong> / 100
                  </span>
                </Td>
                <Td><IntentBadge intent={l.contact?.lead_intent} testId={`lead-intent-badge-${l.lead_id}`} /></Td>
                <Td><PriorityBadge label={l.lead_priority_label} /></Td>
                <Td>
                  <StatusPill status={l.status || "New"} />
                </Td>
                <Td>
                  <span style={{ whiteSpace: "nowrap", fontSize: "0.78rem", color: "#17233D" }}>
                    {formatDate(l.submitted_at || l.created_at)}
                  </span>
                </Td>
                <Td>
                  <button
                    className="btn-ghost"
                    style={{ padding: "0.25rem 0.6rem", minHeight: 30 }}
                    onClick={() => onView(l)}
                    data-testid={`lead-view-${l.lead_id}`}
                  >
                    View
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Badges ---------- */
const BADGE_BASE = {
  display: "inline-block",
  padding: "2px 10px",
  fontSize: "0.72rem",
  letterSpacing: "0.05em",
  whiteSpace: "nowrap",
  lineHeight: 1.6,
  minWidth: 96,
  textAlign: "center",
};

function PriorityBadge({ label }) {
  const map = {
    "High Priority":       { bg: "#1E1968", fg: "#fff" },
    "Medium Priority":     { bg: "#39A5C8", fg: "#fff" },
    "Standard Follow-Up":  { bg: "#eaf2fa", fg: "#173A6A" },
  };
  const s = map[label] || map["Standard Follow-Up"];
  return (
    <span style={{ ...BADGE_BASE, background: s.bg, color: s.fg }} data-testid="priority-badge">
      {label}
    </span>
  );
}

function IntentBadge({ intent, testId }) {
  if (intent === "schedule_plan_review") {
    return (
      <span data-testid={testId} style={{ ...BADGE_BASE, background: "#39A5C8", color: "#fff", minWidth: 156 }}>
        Plan Review Interest
      </span>
    );
  }
  if (intent === "direct_discovery_meeting") {
    return (
      <span data-testid={testId} style={{ ...BADGE_BASE, background: "#1E1968", color: "#fff", minWidth: 156 }}>
        Scheduling Started
      </span>
    );
  }
  return (
    <span data-testid={testId} style={{ ...BADGE_BASE, background: "#f0f4f9", color: "#3a4d70", minWidth: 148 }}>
      General Contact
    </span>
  );
}

function StatusPill({ status }) {
  const map = {
    "New":                 { bg: "#eaf2fa", fg: "#173A6A" },
    "Contacted":           { bg: "#e3ecf6", fg: "#1E1968" },
    "Meeting Scheduled":   { bg: "#d6ecf5", fg: "#134e63" },
    "Closed / Completed":  { bg: "#e6f0e6", fg: "#2f5d2f" },
  };
  const s = map[status] || map["New"];
  return (
    <span style={{ ...BADGE_BASE, background: s.bg, color: s.fg, minWidth: 120 }} data-testid={`status-pill-${status.replace(/[\s\/]+/g, "-")}`}>
      {status}
    </span>
  );
}

/* ---------- Modal ---------- */
function LeadModal({ lead, onClose, onStatusChange }) {
  const c = lead.contact || {};
  const scores = lead.category_scores || {};
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4"
      style={{ background: "rgba(23,35,61,0.55)", overflowY: "auto" }}
      onClick={onClose}
    >
      <div
        className="bg-white max-w-3xl w-full p-8 my-8"
        style={{ maxHeight: "none" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-modal"
      >
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.14em]" style={{ color: "#258FC4", fontWeight: 500 }}>
              {lead.lead_id}
            </div>
            <h2 className="font-serif mt-1" style={{ color: "#1E1968", fontSize: "1.5rem", fontWeight: 600 }}>
              {c.first_name} {c.last_name}
            </h2>
            <div className="text-sm mt-1" style={{ color: "#3a4d70" }}>
              Submitted {formatDate(lead.submitted_at || lead.created_at)}
            </div>
          </div>
          <button className="btn-ghost" onClick={onClose} data-testid="lead-modal-close">Close</button>
        </div>

        {/* Status editor */}
        <Section title="Status">
          <div className="flex items-center gap-3 flex-wrap">
            <StatusPill status={lead.status || "New"} />
            <label className="text-xs" style={{ color: "#3a4d70" }}>
              Update status:
              <select
                className="summit-input ml-2"
                style={{ display: "inline-block", width: "auto", minHeight: 32, padding: "0.25rem 0.5rem" }}
                value={lead.status || "New"}
                onChange={(e) => onStatusChange(e.target.value)}
                data-testid="lead-status-select"
              >
                {ALLOWED_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
        </Section>

        <div className="grid md:grid-cols-2 gap-6 text-sm">
          <div>
            <Section title="Lead Information">
              <Field k="Lead ID" v={lead.lead_id} />
              <Field k="Email" v={c.email} />
              <Field k="Phone" v={c.phone || "—"} />
              <Field k="State" v={c.state} />
              <Field k="Preferred method" v={c.contact_method} />
              <Field k="Best time" v={c.best_time || "—"} />
              <Field k="Lead intent" v={intentLabel(c.lead_intent)} />
              <Field k="Raw intent (internal)" v={c.lead_intent || "general_contact"} readOnly />
              <Field k="Source" v={sourceLabel(lead.source)} readOnly />
              <Field k="Lead attribution" v={attributionLabel(lead.lead_attribution)} readOnly />
              <Field k="Submitted" v={formatDate(lead.submitted_at || lead.created_at)} readOnly />
            </Section>
            <Section title="Profile">
              <Field k="Age" v={lead.answers?.age} />
              <Field k="Partner" v={lead.answers?.partner} />
              <Field k="Retirement status" v={lead.answers?.retirement_status} />
              <Field k="Investable assets" v={lead.answers?.investable_assets} />
            </Section>
          </div>
          <div>
            <Section title="Assessment Summary">
              <div className="font-serif text-3xl" style={{ color: "#1E1968", fontWeight: 600 }} data-testid="modal-readiness-score">
                {lead.trail_score} / 100
              </div>
              <div className="text-sm mt-1" style={{ color: "#173A6A" }}>{lead.band?.label}</div>
              <div className="text-xs mt-1" style={{ color: "#3a4d70" }}>
                Life stage: {inferLifeStage(lead.answers)}
              </div>
              <div className="mt-4 space-y-1.5">
                {CATEGORY_ORDER.map((k) => (
                  <div key={k} className="flex justify-between">
                    <span style={{ color: "#3a4d70" }}>{CATEGORY_LABEL[k]}</span>
                    <span><strong>{scores[k] ?? 0}</strong> / {CATEGORY_MAX[k]}</span>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Lead Priority (Internal)">
              <div className="font-medium" style={{ color: "#1E1968" }}>
                {lead.lead_priority_label} — {lead.lead_priority_score}/100
              </div>
            </Section>

            <Section title="Planning Review Areas">
              <ul className="list-disc pl-5 space-y-1">
                {(lead.review_areas || []).map((r) => <li key={r.key}>{r.label}</li>)}
                {(!lead.review_areas || lead.review_areas.length === 0) && <li>—</li>}
              </ul>
              {(lead.flags || []).length > 0 && (
                <>
                  <div className="text-xs uppercase tracking-[0.14em] mt-4 mb-1" style={{ color: "#258FC4", fontWeight: 500 }}>
                    Discussion topics
                  </div>
                  <ul className="list-disc pl-5 space-y-1">
                    {(lead.flags || []).map((f) => <li key={f.key}>{f.copy}</li>)}
                  </ul>
                </>
              )}
            </Section>
          </div>
        </div>

        {/* Assessment answers (existing raw answers) */}
        {lead.answers && (
          <Section title="Assessment Answers">
            <details>
              <summary className="cursor-pointer text-sm" style={{ color: "#258FC4" }}>Show raw answers</summary>
              <pre
                className="mt-3 text-xs p-3 overflow-x-auto"
                style={{ background: "#F5F8FC", color: "#17233D", maxHeight: 320 }}
                data-testid="raw-answers"
              >
                {JSON.stringify(lead.answers, null, 2)}
              </pre>
            </details>
          </Section>
        )}
      </div>
    </div>
  );
}

const Th = ({ children }) => (
  <th className="text-left px-3 py-2.5 text-xs uppercase tracking-[0.1em] font-medium" style={{ whiteSpace: "nowrap" }}>
    {children}
  </th>
);
const Td = ({ children }) => (
  <td className="px-3 py-3 align-top" style={{ color: "#17233D" }}>{children}</td>
);
const Section = ({ title, children }) => (
  <div className="mb-6">
    <div className="text-xs uppercase tracking-[0.14em] mb-2" style={{ color: "#258FC4", fontWeight: 500 }}>{title}</div>
    <div style={{ color: "#17233D" }}>{children}</div>
  </div>
);
const Field = ({ k, v, readOnly = false }) => (
  <div className="flex justify-between py-1 gap-3" style={{ borderBottom: "1px solid #f0f4f9" }}>
    <span style={{ color: "#3a4d70" }}>
      {k}
      {readOnly && <span title="Read-only" style={{ marginLeft: 4, fontSize: "0.65rem", color: "#7a8ba6" }}>(read-only)</span>}
    </span>
    <span style={{ fontWeight: 500, textAlign: "right" }}>{String(v ?? "—")}</span>
  </div>
);

function sourceLabel(v) {
  if (v === "financial_future_readiness_assessment") return "Financial Future Readiness Assessment";
  return v || "—";
}
function attributionLabel(v) {
  if (v === "financial_future_readiness") return "Financial Future Readiness";
  return v || "—";
}
function intentLabel(v) {
  if (v === "schedule_plan_review") return "Plan Review Interest";
  if (v === "direct_discovery_meeting") return "Scheduling Started";
  return "General Contact";
}
function inferLifeStage(answers) {
  const s = answers?.retirement_status;
  if (s === "10plus") return "young";
  if (s === "6-10yr") return "mid";
  return "pre";
}
